import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { platformSalesReturnApi } from '../../api/platformSalesReturn';
import { message } from 'antd';

// 获取平台代售退货信息列表
export const fetchPlatformSalesReturnList = createAsyncThunk(
  'platformSalesReturn/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await platformSalesReturnApi.getPlatformSalesReturnList(params);
      // 后端返回的是 IPage 格式，包含 records, total, current, size 等字段
      const data = response.data || response;
      return {
        list: data.records || data.list || [],
        total: data.total || 0,
        current: data.current || params.page || 1,
        pageSize: data.size || params.size || 10
      };
    } catch (error) {
      console.error('获取平台代售退货信息列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售退货信息列表失败' });
    }
  }
);

// 获取平台代售退货信息详情
export const fetchPlatformSalesReturnDetail = createAsyncThunk(
  'platformSalesReturn/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesReturnApi.getPlatformSalesReturnDetail(id);
      return response.data || response;
    } catch (error) {
      console.error('获取平台代售退货信息详情失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售退货信息详情失败' });
    }
  }
);

// 创建平台代售退货信息
export const createPlatformSalesReturn = createAsyncThunk(
  'platformSalesReturn/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await platformSalesReturnApi.createPlatformSalesReturn(data);
      message.success('创建退货信息成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '创建退货信息失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 更新平台代售退货信息
export const updatePlatformSalesReturn = createAsyncThunk(
  'platformSalesReturn/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await platformSalesReturnApi.updatePlatformSalesReturn(id, data);
      message.success('更新退货信息成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '更新退货信息失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 删除平台代售退货信息
export const deletePlatformSalesReturn = createAsyncThunk(
  'platformSalesReturn/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesReturnApi.deletePlatformSalesReturn(id);
      message.success('删除退货信息成功');
      return id;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除退货信息失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 根据订单ID查询退货信息
export const fetchReturnByOrderId = createAsyncThunk(
  'platformSalesReturn/fetchByOrderId',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await platformSalesReturnApi.getReturnByOrderId(orderId);
      return response.data || response;
    } catch (error) {
      console.error('根据订单ID查询退货信息失败:', error);
      return rejectWithValue(error.response?.data || { message: '根据订单ID查询退货信息失败' });
    }
  }
);

// 平台代售退货信息 Slice
const platformSalesReturnSlice = createSlice({
  name: 'platformSalesReturn',
  initialState: {
    list: [],
    currentReturn: null,
    returnByOrder: null,
    loading: false,
    error: null,
    pagination: {
      total: 0,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearCurrentReturn: (state) => {
      state.currentReturn = null;
    },
    clearReturnByOrder: (state) => {
      state.returnByOrder = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchPlatformSalesReturnList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlatformSalesReturnList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.list;
        state.pagination.total = action.payload.total;
        state.pagination.current = action.payload.current;
        state.pagination.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPlatformSalesReturnList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取详情
      .addCase(fetchPlatformSalesReturnDetail.fulfilled, (state, action) => {
        state.currentReturn = action.payload;
      })
      
      // 创建退货信息
      .addCase(createPlatformSalesReturn.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.pagination.total += 1;
      })
      
      // 更新退货信息
      .addCase(updatePlatformSalesReturn.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentReturn && state.currentReturn.id === action.payload.id) {
          state.currentReturn = action.payload;
        }
      })
      
      // 删除退货信息
      .addCase(deletePlatformSalesReturn.fulfilled, (state, action) => {
        state.list = state.list.filter(item => item.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentReturn && state.currentReturn.id === action.payload) {
          state.currentReturn = null;
        }
      })
      
      // 根据订单ID查询退货信息
      .addCase(fetchReturnByOrderId.fulfilled, (state, action) => {
        state.returnByOrder = action.payload;
      });
  }
});

export const { setPagination, clearCurrentReturn, clearReturnByOrder } = platformSalesReturnSlice.actions;
export default platformSalesReturnSlice.reducer;

